<template>
  <b-card class="p-1">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <b-form-group class="w-50 mb-0">
        <b-input-group size="sm">
          <b-form-input id="filterInput" v-model="filter" type="search" placeholder="Wyszukaj po nazwie" />
          <b-input-group-append>
            <b-button :disabled="!filter" @click="filter = ''"> Wyczyść </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-button
        :to="{ name: 'navigation-products-create' }"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        type="submit"
        variant="primary"
        class="btn">
        Dodaj
      </b-button>
    </div>
    <b-table
      responsive
      bordered
      striped
      hover
      :items="navigationGroups"
      :fields="fields"
      :filter="filter"
      :filter-included-fields="filterOn">
      <template #cell(order)="row">
        <div class="d-flex align-items-center">
          <feather-icon 
            class="move-icon mr-1" 
            icon="ChevronUpIcon" 
            @click="moveGroupUpById(row.item.id)"
            :class="{ 'disabled-icon': isFirstItem(row.item.id) }" />
          <feather-icon 
            class="move-icon" 
            icon="ChevronDownIcon" 
            @click="moveGroupDownById(row.item.id)"
            :class="{ 'disabled-icon': isLastItem(row.item.id) }" />
        </div>
      </template>
      <template #cell(actions)="row">
        <div class="d-flex flex-column">
          <b-button
            :to="{ name: 'navigation-products-edit', params: { id: row.item.id } }"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="btn table-btn"
            size="sm">
            Edytuj
          </b-button>
          <b-button class="mt-1 table-btn" variant="danger" size="sm" @click="destroy(row.item)"> Usuń </b-button>
        </div>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';

import { allProductElements, destroyProduct, updateProductNavigationOrder } from '@/http/navigation';

export default {
  mixins: [NotificationMixin],

  data() {
    return {
      navigationGroups: [],
      filter: '',
      filterOn: [],
      fields: [
        {
          key: 'name',
          label: 'Nazwa grupy',
          sortable: true,
          sortDirection: 'desc',
        },
        {
          key: 'position',
          label: 'Pozycja',
          sortable: true,
        },
        {
          key: 'order',
          label: 'Kolejność',
          sortable: false,
        },
        { key: 'actions', label: 'Akcje' },
      ],
    };
  },

  async mounted() {
    await this.fetchNavGroups();
  },

  methods: {
    async fetchNavGroups() {
      try {
        const { data } = await allProductElements({
          lang: 'pl',
        });

        // Sortuj po pozycji
        this.navigationGroups = data.data.sort((a, b) => {
          const posA = a.position || 0;
          const posB = b.position || 0;
          return posA - posB;
        });
      } catch (error) {
        console.log(error);
      }
    },
    destroy(navGroup) {
      this.$swal({
        title: 'Jesteś pewny?',
        text: 'Czy chcesz usunąć ten element nawigacji?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          destroyProduct(navGroup.id)
            .then(() => {
              this.showSuccessNotification('Element nawigacji usunięty', 'Pomyślnie usunięto element nawigacji.');
              this.fetchNavGroups();
            })
            .catch(() => {
              this.showErrorNotification(
                'Problem z usunięciem danych',
                'Wystąpił błąd podczas usuwania elementu nawigacji. Skontaktuj się ze swoim developerem.',
              );
            });
        }
      });
    },
    findGroupIndexById(id) {
      return this.navigationGroups.findIndex(group => group.id === id);
    },
    isFirstItem(id) {
      const index = this.findGroupIndexById(id);
      return index === 0;
    },
    isLastItem(id) {
      const index = this.findGroupIndexById(id);
      return index === this.navigationGroups.length - 1;
    },
    async moveGroupUpById(id) {
      const index = this.findGroupIndexById(id);
      if (index === -1 || index === 0) return;
      
      const currentItem = this.navigationGroups[index];
      const previousItem = this.navigationGroups[index - 1];
      
      // Przygotuj dane do aktualizacji - zamień pozycje
      const orderData = this.navigationGroups.map((group, idx) => {
        if (group.id === currentItem.id) {
          return { id: group.id, position: previousItem.position };
        }
        if (group.id === previousItem.id) {
          return { id: group.id, position: currentItem.position };
        }
        return { id: group.id, position: group.position || idx + 1 };
      });
      
      await this.updateOrder(orderData);
    },
    async moveGroupDownById(id) {
      const index = this.findGroupIndexById(id);
      if (index === -1 || index === this.navigationGroups.length - 1) return;
      
      const currentItem = this.navigationGroups[index];
      const nextItem = this.navigationGroups[index + 1];
      
      // Przygotuj dane do aktualizacji - zamień pozycje
      const orderData = this.navigationGroups.map((group, idx) => {
        if (group.id === currentItem.id) {
          return { id: group.id, position: nextItem.position };
        }
        if (group.id === nextItem.id) {
          return { id: group.id, position: currentItem.position };
        }
        return { id: group.id, position: group.position || idx + 1 };
      });
      
      await this.updateOrder(orderData);
    },
    async updateOrder(orderData) {
      try {
        await updateProductNavigationOrder(orderData);
        this.showSuccessNotification('Kolejność zaktualizowana', 'Pomyślnie zaktualizowano kolejność grup nawigacji.');
        // Odśwież dane z serwera
        await this.fetchNavGroups();
      } catch (error) {
        this.showErrorNotification(
          'Problem z aktualizacją kolejności',
          'Nie udało się zaktualizować kolejności. Spróbuj ponownie.',
        );
        // Odśwież dane, żeby przywrócić poprzednią kolejność
        await this.fetchNavGroups();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.move-icon {
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s;
  
  &:hover:not(.disabled-icon) {
    color: #007bff;
  }
  
  &.disabled-icon {
    opacity: 0.3;
    cursor: not-allowed;
  }
}
</style>
